import Message, {localizedMessage} from '../message/Message';
import {isCoupon, isPosaCode} from './util';

import ErrorMessage from '../message/ErrorMessage';
import Overlay from '../overlay/Overlay';
import PropTypes from 'prop-types';
import React from 'react';
import RedeemActions from '../../actions/RedeemActions';
import RedeemContainer from './RedeemContainer';
import SquareButton from '../squareButton/SquareButton';
import {ValidateStageStyled} from '.';
import classNames from 'classnames';
import epicTracking from 'epic-tracking';

class ValidateStage extends React.Component {
  static propTypes = {
    redeemStore: PropTypes.object,
    nextStage: PropTypes.string,
    prefillCode: PropTypes.string,
    config: PropTypes.object,
    isLoggedIn: PropTypes.bool,
    description: PropTypes.string,
    title: PropTypes.string,
    enablePosa: PropTypes.bool,
    posaImage: PropTypes.string,
    posaLink: PropTypes.string,
  };

  constructor(props) {
    super(props);
    this.state = {
      codeInput: props.prefillCode || '', //load prefilled code into form if present
      localErrorMessage: '',
      couldBePosaCode: false,
      showOverlay: false,
    };
  }

  /**
   * Check Code Client Side if code looks like a VBucks Card Code and not Redeem Code
   */
  checkPosaCode = (currentValue) => {
    //if posa is not enabled just return false.
    if (!this.props.enablePosa) return false;
    return isPosaCode(currentValue);
  };

  /**
   * Event handler for form field input
   */
  handleInput = (evt) => {
    const currentValue = evt.target.value;
    this.setState({
      codeInput: currentValue,
    });
  };

  /**
   * Event handler for submitting form with Enter key
   */
  handleEnterSubmit = (evt) => {
    if (evt.keyCode === 13) {
      this.handleSubmit();
    }
  };

  /**
   * Event handler for submitting form through clicking Redeem button
   */
  handleSubmit = () => {
    const code = (this.state.codeInput || '').trim();
    const couldBePosaCode = this.checkPosaCode(code);
    if (this.props.enablePosa && couldBePosaCode && !this.state.showOverlay) {
      this.setState({couldBePosaCode, showOverlay: true});
    } else if (!isCoupon(code)) {
      epicTracking.trackEvent('Interaction', {
        interactionType: 'click',
        eventCategory: 'RedeemCode',
        eventAction: 'inValidCodeShortOrMissing',
      });
      // stop submit if code length under 20 characters
      this.setState({
        localErrorMessage: 'errors.com.epicgames.code.redemption.code_length',
        showOverlay: false,
      });
    } else {
      epicTracking.trackEvent('Interaction', {
        interactionType: 'click',
        eventCategory: 'RedeemCode',
        eventAction: 'validatingCode',
      });
      RedeemActions.validateCode({
        codeInput: this.state.codeInput,
        isLoggedIn: this.props.isLoggedIn,
        talonFlowId: this.props.config.epicgames.talonFlowId,
      });
      RedeemActions.setNextStage(this.props.nextStage);
      this.setState({
        localErrorMessage: '',
      });
    }
  };

  toggleOverlay = () => {
    this.setState({
      showOverlay: !this.state.showOverlay,
    });
  };

  getRedeemRedirectLink = (href) => {
    const accountPageName = localizedMessage('epic.common.account_management');
    const linkClass = 'redeem-redirect-link';
    const {
      redeemStore: {redeemCode},
    } = this.props;
    const codeRedeemRedirect = `${href}?redeemCode=${redeemCode}`;
    return `<a name='redeem-redirect' class=${linkClass} value=${accountPageName} href=${codeRedeemRedirect} target='_blank'>${accountPageName}</a>`;
  };

  componentDidUpdate(prevProps) {
    const {
      redeemStore: {redeemCode, validationError, isInvalidCode},
    } = this.props;
    const {
      redeemStore: {redeemCode: prevRedeemCode, isInvalidCode: prevIsInvalidCode},
    } = prevProps;
    const {localErrorMessage} = this.state;
    // Local error message is client side validation and will be tracked in handleSubmit.
    // Redem store contains server side response and tracking is sent if same error is sent for a different code.
    if (
      !localErrorMessage &&
      validationError &&
      ((isInvalidCode !== prevIsInvalidCode && isInvalidCode) || prevRedeemCode !== redeemCode)
    ) {
      const {code, numericErrorCode} = validationError;
      epicTracking.trackEvent('Interaction', {
        interactionType: 'click',
        eventCategory: 'RedeemCode',
        eventAction: 'inValidCode',
        code,
        numericErrorCode,
      });
    }
  }

  render() {
    let validateStageView; //content to render
    const config = this.props.config || {};
    if (this.props.redeemStore.loading) {
      validateStageView = <div className="redeemLoading" />;
    } else {
      const {
        redeemStore: {validationError},
        description,
        title,
        posaImage,
        enablePosa,
        posaLink,
      } = this.props;
      const isError = !!this.state.localErrorMessage || this.props.redeemStore.isInvalidCode;
      const errorClass = classNames('errorMessage', {hidden: !isError});
      const inputPlaceholder = localizedMessage('epic.code.redemption.validate.input_placeholder');

      const {showOverlay, couldBePosaCode, codeInput} = this.state;

      const msgConfig = config.messages || {};
      const egConfig = config.epicgames || {};
      const egHost = egConfig.host || '';
      const egNamespace = egConfig.namespace || '';
      const codeRedemptionURI = egConfig.codeRedemptionURI || '';

      const upperCaseCode = codeInput.toUpperCase();

      const epicgamesLink = `${egHost}/${egNamespace}/${codeRedemptionURI}`;
      const errorCode = validationError && validationError.code;
      const isSupported = validationError && validationError.isSupported;
      const numericErrorCode = validationError && validationError.numericErrorCode;
      const redirectConfig = {};
      if (!isSupported) {
        redirectConfig.link = this.getRedeemRedirectLink(epicgamesLink);
      }

      validateStageView = (
        <RedeemContainer className="redeemContainer">
          <h1 className="site-block-page-title">
            <Message
              code={
                msgConfig['epic.code.redemption.validate.title'] ||
                'epic.code.redemption.validate.title'
              }
            />
          </h1>
          <div className="site-block">
            <div className="site-block-header">
              <h1 className="site-block-title">
                {title ? title : <Message code="epic.code.redemption.validate.subtitle" />}
              </h1>
              <div className="clearfix" />
            </div>
            {description && <div className="site-block-desc">{description}</div>}
            <input
              value={this.state.codeInput}
              type="text"
              maxLength="23"
              placeholder={inputPlaceholder}
              onChange={this.handleInput}
              onKeyDown={this.handleEnterSubmit}
            />
            <div className={errorClass}>
              {/* display code length error message if code is < 20 characters */}
              {this.state.localErrorMessage ? (
                <Message code={this.state.localErrorMessage} />
              ) : null}
              {/* display error message from RedeemStore if AJAX request is rejected */}
              {this.props.redeemStore.isInvalidCode &&
                this.state.localErrorMessage === '' &&
                errorCode && (
                  <ErrorMessage
                    msg={errorCode}
                    code={numericErrorCode}
                    args={[redirectConfig.link]}
                  />
                )}
            </div>
            <div className="buttonContainer">
              <SquareButton
                handleClick={this.handleSubmit}
                config={{buttonStyle: 'btn-primary', width: '240px', centered: true}}>
                <Message code="epic.common.redeem" />
              </SquareButton>
            </div>
          </div>
          {enablePosa && couldBePosaCode && showOverlay ? (
            <Overlay
              upperCaseCode={upperCaseCode}
              image={posaImage}
              link={posaLink}
              handleSubmit={this.handleSubmit}
              close={this.toggleOverlay}
            />
          ) : null}
        </RedeemContainer>
      );
    }

    return <ValidateStageStyled className="validateStage">{validateStageView}</ValidateStageStyled>;
  }
}

export default ValidateStage;
